const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface SteamScreenshot {
  id: number;
  path_thumbnail: string;
  path_full: string;
}

interface SteamApiResponse {
  movies: any[];
  screenshots: SteamScreenshot[];
}

export interface PriceOverview {
  currency: string;
  initial: number;
  final: number;
  discount_percent: number;
  initial_formatted: string;
  final_formatted: string;
}

export interface Platforms {
  windows: boolean;
  mac: boolean;
  linux: boolean;
}

export interface Metacritic {
  score: number;
  url: string;
}

export interface SteamGameDetails {
  name: string;
  header_image: string;
  short_description: string;
  is_free: boolean;
  platforms?: {
    windows: boolean;
    mac: boolean;
    linux: boolean;
  };
  price_overview?: {
    final: number;
    initial: number;
    discount_percent: number;
  };
  metacritic?: {
    score: number;
    url: string;
  };
  release_date?: {
    coming_soon: boolean;
    date: string;
  };
}

export interface SteamReviews {
  total_reviews: number;
  total_positive: number;
  review_score: number;
  review_score_desc: string;
}

export interface SteamReviewsData {
  global: SteamReviews | null;
  japan: SteamReviews | null;
}

export interface SteamPlayerSummary {
  personastate: number;
  personastate_label: 'offline' | 'online' | 'busy' | 'away' | 'snooze' | 'looking_to_trade' | 'looking_to_play';
  gameextrainfo?: string;  // プレイ中のゲーム名
  gameid?: string;         // プレイ中のゲームID（Steam）
}

export const getSteamGameVideos = async (appId: string): Promise<{ videos: any[], screenshots: SteamScreenshot[] }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/steam/videos/${appId}/`);
    const data: SteamApiResponse = await response.json();
    return {
      videos: data.movies || [],
      screenshots: data.screenshots || []
    };
  } catch (error) {
    console.error('Steamのメディア取得に失敗しました:', error);
    return { videos: [], screenshots: [] };
  }
};

export const getSteamGameDetails = async (appId: string): Promise<SteamGameDetails | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/steam/details/${appId}/`);
    if (!response.ok) {
      throw new Error('Steam details fetch failed');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Steamのゲーム詳細の取得に失敗しました:', error);
    return null;
  }
};

// Steamレビュー取得（グローバル＋日本）
// コンポーネント側でglobalのみ使うか、両方使うかを選択
export const getSteamReviews = async (appId: string): Promise<SteamReviewsData> => {
  try {
    const response = await fetch(`${API_BASE_URL}/steam/reviews/${appId}/`);
    if (!response.ok) {
      throw new Error('Steam reviews fetch failed');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Steamのレビュー情報の取得に失敗しました:', error);
    // エラー時は両方nullを返す
    return { global: null, japan: null };
  }
};

// Steamプレイヤーのオンラインステータスを取得
export const getSteamPlayerSummary = async (steamId: string): Promise<SteamPlayerSummary | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/steam/player/${steamId}/`);
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Steamプレイヤー情報の取得に失敗しました:', error);
    return null;
  }
};

// SteamのアプリIDからPULSEのgame_idを取得
export const getGameIdBySteamId = async (steamAppId: string): Promise<number | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/steam/game/${steamAppId}/`);
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data.game_id;
  } catch (error) {
    console.error('SteamIDからゲームIDの取得に失敗しました:', error);
    return null;
  }
};